import { brokersService } from '@/services';
import { UpdateBrokerProductDto } from '@/services/brokers/types';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { unsavedProductsMessage } from '../utils/update-broker-products-result.util';

export function useUpdateBrokerProducts(brokerId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (products: UpdateBrokerProductDto[]) => {
      const result = await brokersService.updateBrokerProducts(
        brokerId,
        products
      );
      if (!result.success) {
        throw new Error(result.error || 'Falha ao atualizar produtos');
      }
      const unsaved = unsavedProductsMessage(products, result.data);
      if (unsaved) throw new Error(unsaved);
      return result;
    },
    onMutate: async (updates: UpdateBrokerProductDto[]) => {
      await queryClient.cancelQueries({
        queryKey: ['broker-products', brokerId],
      });

      const previous = queryClient.getQueriesData({
        queryKey: ['broker-products', brokerId],
      });

      const toUiStatus = (status: UpdateBrokerProductDto['status']) =>
        status === 'ACTIVE' ? 'active' : 'inactive';

      const matchesUpdate = (
        item: { id?: string; productId?: string; productCode?: string },
        update: UpdateBrokerProductDto
      ) => {
        const itemId = item.id ?? item.productId;
        const updId = update.id ?? update.productId;
        if (itemId && updId && itemId === updId) return true;
        if (
          item.productId &&
          update.productId &&
          item.productId === update.productId
        )
          return true;
        if (
          item.productCode &&
          update.productCode &&
          item.productCode === update.productCode
        )
          return true;
        return false;
      };

      queryClient.setQueriesData(
        { queryKey: ['broker-products', brokerId] },
        (old: unknown) => {
          if (!old || typeof old !== 'object') return old;

          const record = old as {
            data?: Array<{
              id?: string;
              productId?: string;
              productCode?: string;
              status?: 'active' | 'inactive';
              subProducts?: Array<{
                id?: string;
                productId?: string;
                productCode?: string;
                status?: 'active' | 'inactive';
              }>;
            }>;
            meta?: unknown;
          };

          if (!Array.isArray(record.data)) return old;

          const nextData = record.data.map(item => {
            const matched = updates.find(u => matchesUpdate(item, u));
            const nextStatus = matched
              ? toUiStatus(matched.status)
              : item.status;

            const nextSubProducts = Array.isArray(item.subProducts)
              ? item.subProducts.map(sub => {
                  const matchedSub = updates.find(u => matchesUpdate(sub, u));
                  return matchedSub
                    ? { ...sub, status: toUiStatus(matchedSub.status) }
                    : sub;
                })
              : item.subProducts;

            return {
              ...item,
              status: (nextStatus ?? item.status) as
                | 'active'
                | 'inactive'
                | undefined,
              subProducts: nextSubProducts,
            };
          });

          return { ...record, data: nextData };
        }
      );

      return { previous };
    },
    onError: (_err, _updates, context) => {
      if (!context?.previous) return;
      context.previous.forEach(([key, data]) => {
        queryClient.setQueryData(key, data);
      });
    },
    // Sempre relê do servidor: o que aparece depois de salvar é o que ficou
    // gravado (no InsureMO ou no motor), não o update otimista.
    onSettled: () =>
      queryClient.invalidateQueries({
        queryKey: ['broker-products', brokerId],
      }),
  });
}
